import "@/App.css";
import { BookOpen, PenTool, AlertTriangle, CheckCircle, XCircle, GraduationCap, LayoutTemplate, Users, FileText, MessageSquare, Target, Lightbulb, ChevronDown } from "lucide-react";

// IFFD Logo URL
const IFFD_LOGO = "https://customer-assets.emergentagent.com/job_casemethod/artifacts/5b948tay_iffd-Inspiring-families-4.png";

// Navigation Component
const Navigation = () => {
  const links = [
    { href: "#document", label: "The Document" },
    { href: "#teaching-note", label: "Teaching Note" },
    { href: "#mistakes", label: "What to Avoid" },
  ];

  return (
    <nav data-testid="main-navigation" className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="#hero" data-testid="nav-logo" className="flex items-center">
          <img src={IFFD_LOGO} alt="IFFD - Inspiring Families" className="h-14 w-auto" />
        </a>
        <div className="hidden md:flex items-center gap-8">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.href.slice(1)}`}
              className="text-gray-600 hover:text-[#1E88E5] transition-colors duration-300 text-sm font-semibold uppercase tracking-wide"
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" data-testid="hero-section" className="min-h-screen pt-24 flex items-center bg-gradient-to-b from-white to-gray-100">
      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Prominent Logo */}
        <div className="flex justify-center mb-12">
          <img 
            src={IFFD_LOGO} 
            alt="IFFD - Inspiring Families" 
            className="h-32 md:h-44 w-auto"
          />
        </div>
        
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <p className="font-mono text-sm text-[#1E88E5] tracking-widest uppercase font-bold">Pedagogical Guide</p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light text-gray-900 leading-tight">
                How to Write an Effective <em className="font-semibold text-[#1E88E5]">Case Study</em>
              </h1>
            </div>
            <p className="text-gray-600 text-lg leading-relaxed max-w-lg">
              Master the art of creating cases that force students to think, analyze, and decide. 
              Learn the structure used by the world's top business schools.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#document"
                data-testid="cta-primary"
                className="inline-flex items-center gap-2 bg-[#1E88E5] text-white px-8 py-4 rounded-full font-bold hover:bg-[#1565C0] transition-colors duration-300 shadow-lg shadow-[#1E88E5]/30 text-lg"
              >
                <BookOpen className="w-6 h-6" />
                Start Learning
              </a>
              <a
                href="#teaching-note"
                data-testid="cta-secondary"
                className="inline-flex items-center gap-2 border-2 border-[#1E88E5] text-[#1E88E5] px-8 py-4 rounded-full font-bold hover:bg-[#1E88E5] hover:text-white transition-all duration-300 text-lg"
              >
                <LayoutTemplate className="w-6 h-6" />
                View Teaching Note
              </a>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="absolute -top-8 -left-8 w-64 h-64 bg-[#1E88E5]/10 rounded-full blur-3xl"></div>
            <img
              src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=800&q=80"
              alt="Professor teaching case method"
              className="rounded-2xl shadow-2xl relative z-10 w-full h-[450px] object-cover border-4 border-white"
            />
            <div className="absolute -bottom-6 -right-6 bg-[#1E88E5] p-6 rounded-xl shadow-xl z-20 max-w-xs">
              <p className="font-serif text-lg text-white italic">"A case is a half-story that the student must complete."</p>
              <p className="text-white/90 text-sm mt-2 font-medium">— Harvard Business School</p>
            </div>
          </div>
        </div>
      </div>
      <a href="#document" className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-10 h-10 text-[#1E88E5]" />
      </a>
    </section>
  );
};

// Timeline Item Component
const TimelineItem = ({ number, title, subtitle, children, icon: Icon, isLast }) => {
  return (
    <div className="relative pl-16 pb-12">
      {!isLast && <div className="timeline-line"></div>}
      <div className="timeline-dot">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="space-y-4">
        <div>
          <span className="font-mono text-xs text-[#1E88E5] tracking-widest font-bold">{number}</span>
          <h3 className="font-serif text-2xl md:text-3xl text-gray-900 font-semibold mt-1">{title}</h3>
          {subtitle && <p className="text-gray-500 text-sm mt-1">{subtitle}</p>}
        </div>
        <div className="text-gray-600 leading-relaxed space-y-3">
          {children}
        </div>
      </div>
    </div>
  );
};

// Bullet Point Component
const BulletPoint = ({ title, children }) => (
  <div className="flex gap-3">
    <CheckCircle className="w-5 h-5 text-[#4CAF50] flex-shrink-0 mt-0.5" />
    <div>
      <span className="font-semibold text-gray-900">{title}:</span>{" "}
      <span className="text-gray-600">{children}</span>
    </div>
  </div>
);

// Document Section
const DocumentSection = () => {
  return (
    <section id="document" data-testid="document-section" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16 space-y-4">
          <p className="font-mono text-sm text-[#1E88E5] tracking-widest uppercase font-bold">Part 1</p>
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 font-semibold">
            The Case Document
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Student material. A narrative that stops just before the resolution, 
            forcing the student to make the decision.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          <TimelineItem
            number="A"
            title="The Opening"
            subtitle="The Hook"
            icon={Target}
          >
            <p className="mb-4 text-gray-600">
              This is the most critical part to capture attention. It must clearly establish:
            </p>
            <div className="space-y-3 bg-gray-50 p-6 rounded-xl border-2 border-gray-200">
              <BulletPoint title="The Protagonist">
                A specific person with name and title, not an abstract organization.
              </BulletPoint>
              <BulletPoint title="The Setting">
                The exact time and place (temporal context).
              </BulletPoint>
              <BulletPoint title="The Action Trigger">
                The immediate challenge or crisis that forces the protagonist to act. 
                It must be clear that there is an urgent decision to make.
              </BulletPoint>
            </div>
          </TimelineItem>

          <TimelineItem
            number="B"
            title="The Case Body"
            subtitle="Narrative and Context"
            icon={FileText}
          >
            <p className="mb-4 text-gray-600">
              Here the necessary information for analysis is developed, 
              generally written in past tense with a neutral tone.
            </p>
            <div className="space-y-3 bg-gray-50 p-6 rounded-xl border-2 border-gray-200">
              <BulletPoint title="Background">
                Company history or industry context relevant to the problem.
              </BulletPoint>
              <BulletPoint title="Problem Development">
                The sequence of events that led to the current situation.
              </BulletPoint>
              <BulletPoint title="Conflicting Perspectives">
                Quotes or testimonies from different stakeholders (employees, family members, partners) 
                to reveal biases and add "productive ambiguity" — incomplete or 
                contradictory information that forces judgment.
              </BulletPoint>
            </div>
          </TimelineItem>

          <TimelineItem
            number="C"
            title="The Closing"
            subtitle="Return to Decision Point"
            icon={Lightbulb}
          >
            <div className="space-y-3 bg-gray-50 p-6 rounded-xl border-2 border-gray-200">
              <BulletPoint title="Return to the Trigger">
                The narrative must not resolve. The ending should bring the reader back 
                to the initial decision point, reinforcing urgency: 
                <em className="text-[#1E88E5] font-semibold"> "What should the protagonist do now?"</em>
              </BulletPoint>
              <BulletPoint title="No Solution">
                Do not include what actually happened or the outcome; 
                the case must remain "open."
              </BulletPoint>
            </div>
          </TimelineItem>

          <TimelineItem
            number="D"
            title="Exhibits"
            subtitle="Supporting Materials"
            icon={LayoutTemplate}
            isLast={true}
          >
            <div className="bg-gray-50 p-6 rounded-xl border-2 border-gray-200">
              <p className="text-gray-600">
                Supporting data (financial tables, organizational charts, press clippings) 
                placed at the end of the document. They must be 
                <strong className="text-gray-900"> clearly labeled</strong> and 
                <strong className="text-gray-900"> referenced in the main text</strong> to 
                avoid interrupting the narrative.
              </p>
            </div>
          </TimelineItem>
        </div>
      </div>
    </section>
  );
};

// Bento Card Component
const BentoCard = ({ title, description, icon: Icon, className = "" }) => (
  <div 
    className={`card-hover bg-white p-6 rounded-xl border-2 border-gray-200 shadow-sm ${className}`}
    data-testid={`bento-card-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-lg bg-[#1E88E5] flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="space-y-2">
        <h4 className="font-serif text-xl text-gray-900 font-semibold">{title}</h4>
        <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

// Teaching Note Section
const TeachingNoteSection = () => {
  const cards = [
    {
      title: "Synopsis",
      description: "A brief summary of the situation and context to orient the instructor.",
      icon: FileText,
    },
    {
      title: "Learning Objectives",
      description: "Clear and measurable goals. E.g., 'Identify the tension between ethics and profitability'.",
      icon: Target,
    },
    {
      title: "Teaching Strategy",
      description: "Suggestions on how to open the discussion and a Blackboard Plan to organize student comments.",
      icon: LayoutTemplate,
    },
    {
      title: "Discussion Questions",
      description: "A curated list of trigger questions to guide Socratic debate in class.",
      icon: MessageSquare,
    },
    {
      title: "Analysis",
      description: "Linking case facts with academic theories or relevant management literature.",
      icon: BookOpen,
    },
    {
      title: "Epilogue",
      description: "'What Happened Next' — Optionally reveals what actually happened, noting it's not necessarily the only correct answer.",
      icon: Lightbulb,
    },
  ];

  return (
    <section id="teaching-note" data-testid="teaching-note-section" className="py-24 bg-gray-100">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16 space-y-4">
          <p className="font-mono text-sm text-[#1E88E5] tracking-widest uppercase font-bold">Part 2</p>
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 font-semibold">
            The Teaching Note
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Instructor material. It's the "operating system" of the case. 
            A case is not a complete pedagogical tool without it.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card, index) => (
            <BentoCard key={index} {...card} />
          ))}
        </div>

        {/* Visual representation of blackboard plan */}
        <div className="mt-16 bg-[#1E88E5] rounded-2xl p-8 md:p-12 shadow-xl">
          <h3 className="font-serif text-2xl text-white mb-8 text-center font-semibold">
            Blackboard Plan Example
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/20 backdrop-blur rounded-xl p-6">
              <h4 className="font-mono text-white text-sm mb-4 tracking-widest font-bold">FACTS</h4>
              <ul className="text-white/90 space-y-2 text-sm">
                <li>• Who is the protagonist?</li>
                <li>• What is the temporal context?</li>
                <li>• What data do we have?</li>
              </ul>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-xl p-6">
              <h4 className="font-mono text-white text-sm mb-4 tracking-widest font-bold">PROBLEMS</h4>
              <ul className="text-white/90 space-y-2 text-sm">
                <li>• What is the central dilemma?</li>
                <li>• Which interests are in conflict?</li>
                <li>• What information is missing?</li>
              </ul>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-xl p-6">
              <h4 className="font-mono text-white text-sm mb-4 tracking-widest font-bold">SOLUTIONS</h4>
              <ul className="text-white/90 space-y-2 text-sm">
                <li>• What alternatives exist?</li>
                <li>• What are the risks?</li>
                <li>• What would you recommend?</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Error Item Component
const ErrorItem = ({ mistake, reason }) => (
  <div className="grid md:grid-cols-2 gap-4 py-6 border-b border-gray-200 last:border-0">
    <div className="flex items-start gap-3">
      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
      <span className="font-semibold text-gray-900">{mistake}</span>
    </div>
    <p className="text-gray-500 text-sm md:pl-8">{reason}</p>
  </div>
);

// What NOT to Include Section
const MistakesSection = () => {
  const errors = [
    {
      mistake: "Explicit learning objectives",
      reason: "This biases the student before they analyze the case on their own.",
    },
    {
      mistake: "Assignment questions",
      reason: "These belong in the Teaching Note so the professor can decide which to use.",
    },
    {
      mistake: "The author's solution or analysis",
      reason: "Eliminates the opportunity for students to develop their own judgment and critical thinking.",
    },
    {
      mistake: "Moralizing language or value judgments",
      reason: "The case must be neutral; ethical conclusions should be drawn by the student.",
    },
    {
      mistake: "The actual outcome of the story",
      reason: "Knowing the ending turns analysis into a validation exercise, not a decision-making one.",
    },
  ];

  return (
    <section id="mistakes" data-testid="mistakes-section" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16 space-y-4">
          <p className="font-mono text-sm text-[#1E88E5] tracking-widest uppercase font-bold">Part 3</p>
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 font-semibold">
            What <em className="text-red-600 not-italic">NOT</em> to Include
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            To meet quality standards, the document the student receives 
            must not contain these elements.
          </p>
        </div>

        <div className="bg-red-50 rounded-2xl p-8 border-2 border-red-200">
          <div className="grid md:grid-cols-2 gap-4 mb-8 pb-4 border-b border-red-200">
            <h4 className="font-mono text-red-600 text-sm tracking-widest font-bold">THE MISTAKE</h4>
            <h4 className="font-mono text-gray-400 text-sm tracking-widest font-bold md:pl-8">WHY IT FAILS</h4>
          </div>
          {errors.map((error, index) => (
            <ErrorItem key={index} {...error} />
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-[#1E88E5] text-white px-6 py-3 rounded-full font-semibold shadow-lg">
            <AlertTriangle className="w-5 h-5" />
            <span>Remember: A case is a "half-story"</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-16 bg-gray-900">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-4">
            <img src={IFFD_LOGO} alt="IFFD - Inspiring Families" className="h-24 w-auto" />
            <p className="text-gray-400 max-w-md">
              This pedagogical guide is based on quality standards from the world's 
              leading business schools.
            </p>
          </div>
          <div className="flex flex-col md:items-end gap-4">
            <div className="flex gap-6">
              <a href="#document" className="text-gray-400 hover:text-[#64B5F6] transition-colors font-medium">
                The Document
              </a>
              <a href="#teaching-note" className="text-gray-400 hover:text-[#64B5F6] transition-colors font-medium">
                Teaching Note
              </a>
              <a href="#mistakes" className="text-gray-400 hover:text-[#64B5F6] transition-colors font-medium">
                What to Avoid
              </a>
            </div>
            <p className="text-gray-600 text-sm">
              © 2026 IFFD - Inspiring Families
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App bg-white min-h-screen">
      <Navigation />
      <HeroSection />
      <DocumentSection />
      <TeachingNoteSection />
      <MistakesSection />
      <Footer />
    </div>
  );
}

export default App;
